import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import ImageUpload from './ImageUpload';
import TranslatableItemFields from './TranslatableItemFields';
import AvailabilitySchedule from './AvailabilitySchedule';
import { storage } from '../utils/storage';
import './ItemForm.css';

const DRAFT_DEBOUNCE_MS = 1000;
const MODIFIER_BATCH_SIZE = 6;

const getDraftKey = (draftKey) => {
  const restaurantId = storage.get('selectedRestaurantId') || 'default';
  return `itemFormDraft_${restaurantId}_${draftKey}`;
};

const ItemForm = ({
  initialData,
  categories,
  modifierGroups,
  inventoryItems = [],
  onSubmit,
  onCancel,
  onDirtyChange,
  submitLabel,
  title,
  draftKey,
}) => {
  const { t } = useTranslation();

  const defaultFormData = {
    categoryId: '',
    name: '',
    description: '',
    price: '',
    preparationTime: 15,
    tags: '',
    allergens: '',
    imageUrl: '',
    translations: {},
    availability: null,
    modifierGroups: [],
    recipe: [],
    isCombo: false,
    comboItems: { fixed: [], groups: [] },
    isFeatured: false,
    isDailySpecial: false,
    isPromotion: false,
    originalPrice: '',
    promotionType: '',
    promotionBadge: '',
    highlightColor: '',
    promotionValidFrom: '',
    promotionValidTo: '',
    promotionSchedule: null,
    storyOrder: 0,
  };

  // Check for saved draft on mount
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const draftAppliedRef = useRef(false);

  const loadDraft = useCallback(() => {
    if (!draftKey) return null;
    try {
      const stored = storage.get(getDraftKey(draftKey));
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (parsed && parsed.formData && parsed.savedAt) {
        // Discard drafts older than 7 days
        const age = Date.now() - parsed.savedAt;
        if (age > 7 * 24 * 60 * 60 * 1000) {
          storage.remove(getDraftKey(draftKey));
          return null;
        }
        return parsed;
      }
    } catch {
      // Corrupted draft — remove it
      storage.remove(getDraftKey(draftKey));
    }
    return null;
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      storage.remove(getDraftKey(draftKey));
    }
  }, [draftKey]);

  // Initialize form state, checking for draft
  const [formData, setFormData] = useState(() => {
    const base = { ...defaultFormData, ...initialData };
    if (!draftKey || draftAppliedRef.current) return base;
    const draft = loadDraft();
    if (draft) {
      draftAppliedRef.current = true;
      return { ...base, ...draft.formData };
    }
    return base;
  });

  // Show draft banner after mount if draft was restored
  useEffect(() => {
    if (draftAppliedRef.current) {
      setShowDraftBanner(true);
    }
  }, []);

  // Debounced autosave to localStorage
  const saveDraftTimeoutRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    if (!draftKey) return;

    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
    }

    saveDraftTimeoutRef.current = setTimeout(() => {
      try {
        const dataToSave = { ...formDataRef.current };
        // Don't save imageUrl in draft (can be large/stale)
        delete dataToSave.imageUrl;
        storage.set(
          getDraftKey(draftKey),
          JSON.stringify({ formData: dataToSave, savedAt: Date.now() })
        );
      } catch {
        // localStorage full or unavailable — silently skip
      }
    }, DRAFT_DEBOUNCE_MS);

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, [formData, draftKey]);

  const handleDiscardDraft = useCallback(() => {
    clearDraft();
    setShowDraftBanner(false);
    setFormData({ ...defaultFormData, ...initialData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearDraft, initialData]);

  // Collapsible sections state — basicInfo always open by default
  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: false,
    image: true,
    recipe: true,
    combo: true,
    tagsAllergens: true,
    availability: true,
    modifiers: true,
    promotions: true,
  });

  const toggleSection = useCallback((section) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Helper State for Combo Builder
  const [selectedFixedCategory, setSelectedFixedCategory] = useState('');
  const [selectedFixedItem, setSelectedFixedItem] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', min: 1, max: 1, items: [] });
  const [selectedGroupCategory, setSelectedGroupCategory] = useState('');
  const [selectedGroupItem, setSelectedGroupItem] = useState('');

  // Helper State for Recipe Builder
  const [selectedIngredientId, setSelectedIngredientId] = useState('');
  const [ingredientQty, setIngredientQty] = useState('');
  const [ingredientUnit, setIngredientUnit] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        isCombo: initialData.isCombo || false,
        comboItems: initialData.comboItems || { fixed: [], groups: [] },
        recipe: initialData.recipe || [],
        isFeatured: initialData.isFeatured || false,
        isDailySpecial: initialData.isDailySpecial || false,
        isPromotion: initialData.isPromotion || false,
        originalPrice: initialData.originalPrice || '',
        promotionType: initialData.promotionType || '',
        promotionBadge: initialData.promotionBadge || '',
        highlightColor: initialData.highlightColor || '',
        promotionValidFrom: initialData.promotionValidFrom || '',
        promotionValidTo: initialData.promotionValidTo || '',
        promotionSchedule: initialData.promotionSchedule || null,
        storyOrder: initialData.storyOrder || 0,
      }));
    }
  }, [initialData]);

  // Track dirty state for unsaved changes warning
  const initialSnapshotRef = useRef(null);

  useEffect(() => {
    if (initialSnapshotRef.current === null) {
      initialSnapshotRef.current = JSON.stringify(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (onDirtyChange && initialSnapshotRef.current !== null) {
      const isDirty = JSON.stringify(formData) !== initialSnapshotRef.current;
      onDirtyChange(isDirty);
    }
  }, [formData, onDirtyChange]);

  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.categoryId) {
      newErrors.categoryId = t('menu.validation.categoryRequired');
    }
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('menu.validation.nameRequired');
    }
    const parsedPrice = parseFloat(formData.price);
    if (
      formData.price === '' ||
      formData.price === null ||
      formData.price === undefined ||
      isNaN(parsedPrice)
    ) {
      newErrors.price = t('menu.validation.priceRequired');
    } else if (parsedPrice < 0) {
      newErrors.price = t('menu.validation.priceNegative');
    }
    if (!formData.isCombo && parseInt(formData.preparationTime, 10) < 0) {
      newErrors.preparationTime = t('menu.validation.prepTimeNegative');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Auto-expand collapsed sections that contain error fields
      const sectionMap = {
        categoryId: 'basicInfo',
        name: 'basicInfo',
        price: 'basicInfo',
        preparationTime: 'basicInfo',
      };
      const sectionsToOpen = new Set();
      Object.keys(newErrors).forEach((field) => {
        const section = sectionMap[field];
        if (section && collapsedSections[section]) {
          sectionsToOpen.add(section);
        }
      });
      if (sectionsToOpen.size > 0) {
        setCollapsedSections((prev) => {
          const updated = { ...prev };
          sectionsToOpen.forEach((s) => {
            updated[s] = false;
          });
          return updated;
        });
      }
      return;
    }
    setErrors({});
    clearDraft();
    setShowDraftBanner(false);
    // Normalize tags/allergens to strings before submit
    const normalizedData = {
      ...formData,
      tags: Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags || '',
      allergens: Array.isArray(formData.allergens)
        ? formData.allergens.join(', ')
        : formData.allergens || '',
    };
    onSubmit(normalizedData);
  };

  // Lazy loading for modifier groups via IntersectionObserver
  const [visibleModifierCount, setVisibleModifierCount] = useState(MODIFIER_BATCH_SIZE);
  const modifierSentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = modifierSentinelRef.current;
    if (!sentinel || collapsedSections.modifiers) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleModifierCount((prev) => prev + MODIFIER_BATCH_SIZE);
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [collapsedSections.modifiers, visibleModifierCount]);

  const toggleModifierGroup = (groupId) => {
    const currentGroups = formData.modifierGroups || [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter((id) => id !== groupId)
      : [...currentGroups, groupId];
    setFormData({ ...formData, modifierGroups: newGroups });
  };

  // --- COMBO FUNCTIONS ---
  const addFixedItem = () => {
    if (!selectedFixedItem) return;
    const cat = categories.find((c) => c.categoryId === selectedFixedCategory);
    const item = cat?.items.find((i) => i.itemId === selectedFixedItem);
    if (!item) return;

    const newFixed = [
      ...(formData.comboItems.fixed || []),
      { itemId: item.itemId, name: item.name, quantity: 1 },
    ];
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, fixed: newFixed },
    });
    setSelectedFixedItem('');
  };

  const removeFixedItem = (index) => {
    const newFixed = [...(formData.comboItems.fixed || [])];
    newFixed.splice(index, 1);
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, fixed: newFixed },
    });
  };

  const addGroupItem = () => {
    if (!selectedGroupItem) return;
    const cat = categories.find((c) => c.categoryId === selectedGroupCategory);
    const item = cat?.items.find((i) => i.itemId === selectedGroupItem);
    if (!item) return;

    if (newGroup.items.some((i) => i.itemId === item.itemId)) return;

    setNewGroup({
      ...newGroup,
      items: [...newGroup.items, { itemId: item.itemId, name: item.name, extraPrice: 0 }],
    });
    setSelectedGroupItem('');
  };

  const addGroup = () => {
    if (!newGroup.name || newGroup.items.length === 0) return;
    const newGroups = [...(formData.comboItems.groups || []), { ...newGroup }];
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, groups: newGroups },
    });
    setNewGroup({ name: '', min: 1, max: 1, items: [] });
  };

  const removeGroup = (index) => {
    const newGroups = [...(formData.comboItems.groups || [])];
    newGroups.splice(index, 1);
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, groups: newGroups },
    });
  };

  // --- RECIPE FUNCTIONS ---
  const addIngredient = () => {
    if (!selectedIngredientId || !ingredientQty) return;

    const invItem = inventoryItems.find((i) => i.itemId === selectedIngredientId);
    if (!invItem) return;

    const newRecipe = [
      ...(formData.recipe || []),
      {
        inventoryItemId: selectedIngredientId,
        quantity: parseFloat(ingredientQty),
        unit: ingredientUnit || invItem.unit,
        name: invItem.name,
        cost: invItem.unitPrice ? invItem.unitPrice * parseFloat(ingredientQty) : 0,
      },
    ];

    setFormData({ ...formData, recipe: newRecipe });
    setSelectedIngredientId('');
    setIngredientQty('');
    setIngredientUnit('');
  };

  const removeIngredient = (index) => {
    const newRecipe = [...(formData.recipe || [])];
    newRecipe.splice(index, 1);
    setFormData({ ...formData, recipe: newRecipe });
  };

  const calculateEstimatedCost = () => {
    if (!formData.recipe) return 0;
    return formData.recipe.reduce((sum, item) => sum + (item.cost || 0), 0);
  };

  const handleSelectIngredient = (e) => {
    const id = e.target.value;
    setSelectedIngredientId(id);
    const item = inventoryItems.find((i) => i.itemId === id);
    if (item) {
      setIngredientUnit(item.unit);
    } else {
      setIngredientUnit('');
    }
  };

  return (
    <div className="card">
      {title && <h3>{title}</h3>}
      <form onSubmit={handleSubmit} noValidate>
        {showDraftBanner && (
          <div className="draft-banner" role="status">
            <span>{t('menu.draftRestored')}</span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={handleDiscardDraft}>
              {t('menu.draftDiscard')}
            </button>
          </div>
        )}
        <p className="required-legend">
          <span className="required">*</span> {t('common.requiredFieldsLegend')}
        </p>

        {/* ── Tipo de producto (pill toggle) ── */}
        <div className="form-section">
          <div className="item-type-toggle" role="group" aria-label={t('menu.productType')}>
            <button
              type="button"
              className={!formData.isCombo ? 'active' : ''}
              onClick={() => setFormData({ ...formData, isCombo: false })}
              aria-pressed={!formData.isCombo}
            >
              Producto estándar
            </button>
            <button
              type="button"
              className={formData.isCombo ? 'active' : ''}
              onClick={() => setFormData({ ...formData, isCombo: true })}
              aria-pressed={formData.isCombo}
            >
              Combo
            </button>
          </div>
        </div>

        {/* ── Información Básica ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.basicInfo ? 'collapsed' : ''}`}
            onClick={() => toggleSection('basicInfo')}
            aria-expanded={!collapsedSections.basicInfo}
          >
            <h2>Información Básica</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.basicInfo ? 'section-body--collapsed' : ''}`}
          >
            <div className="form-group">
              <label htmlFor="item-category">
                {t('menu.category')} <span className="required">*</span>
              </label>
              <select
                id="item-category"
                value={formData.categoryId}
                onChange={(e) => {
                  setFormData({ ...formData, categoryId: e.target.value });
                  if (errors.categoryId) setErrors((prev) => ({ ...prev, categoryId: undefined }));
                }}
              >
                <option value="">{t('menu.selectCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && <span className="field-error">{errors.categoryId}</span>}
            </div>

            <TranslatableItemFields
              item={formData}
              onChange={(updatedItem) => {
                setFormData(updatedItem);
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              nameRequired={true}
              nameError={errors.name}
            />

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="item-price">
                  {t('menu.price')} <span className="required">*</span>
                </label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    id="item-price"
                    type="number"
                    step="1"
                    min="0"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: e.target.value });
                      if (errors.price) setErrors((prev) => ({ ...prev, price: undefined }));
                    }}
                    placeholder="0"
                  />
                </div>
                {errors.price && <span className="field-error">{errors.price}</span>}
              </div>

              {!formData.isCombo && (
                <div className="form-group">
                  <label htmlFor="item-prep-time">
                    {t('menu.preparationTime')} (min) <span className="required">*</span>
                  </label>
                  <input
                    id="item-prep-time"
                    type="number"
                    min="0"
                    value={formData.preparationTime}
                    onChange={(e) => {
                      setFormData({ ...formData, preparationTime: e.target.value });
                      if (errors.preparationTime)
                        setErrors((prev) => ({ ...prev, preparationTime: undefined }));
                    }}
                  />
                  {errors.preparationTime && (
                    <span className="field-error">{errors.preparationTime}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Imagen ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.image ? 'collapsed' : ''}`}
            onClick={() => toggleSection('image')}
            aria-expanded={!collapsedSections.image}
          >
            <h2>
              {t('menu.coverImage')} <span className="optional-hint">({t('common.optional')})</span>
            </h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.image ? 'section-body--collapsed' : ''}`}
          >
            <ImageUpload
              resourceType="items"
              resourceId={formData.itemId || `temp-${Date.now()}`}
              currentImageUrl={formData.imageUrl}
              onImageUploaded={(url) => setFormData({ ...formData, imageUrl: url })}
            />
          </div>
        </div>

        {/* ── Receta e Ingredientes (solo estándar) ── */}
        {!formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.recipe ? 'collapsed' : ''}`}
              onClick={() => toggleSection('recipe')}
              aria-expanded={!collapsedSections.recipe}
            >
              <h2>Receta e Ingredientes</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.recipe ? 'section-body--collapsed' : ''}`}
            >
              <div className="recipe-builder">
                <h4>
                  <span>Ingredientes</span>
                  <span className="cost-label">
                    Costo Est.: ${calculateEstimatedCost().toFixed(2)}
                  </span>
                </h4>

                <div className="recipe-form-row">
                  <div className="recipe-form-field flex-2">
                    <label htmlFor="recipe-ingredient">Ingrediente (Inventario)</label>
                    <select
                      id="recipe-ingredient"
                      value={selectedIngredientId}
                      onChange={handleSelectIngredient}
                    >
                      <option value="">Seleccionar Ingrediente</option>
                      {inventoryItems.map((item) => (
                        <option key={item.itemId} value={item.itemId}>
                          {item.name} ({item.unit})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="recipe-form-field flex-1">
                    <label htmlFor="recipe-qty">Cant.</label>
                    <input
                      id="recipe-qty"
                      type="number"
                      step="0.01"
                      value={ingredientQty}
                      onChange={(e) => setIngredientQty(e.target.value)}
                    />
                  </div>
                  <div className="recipe-form-field flex-1">
                    <label htmlFor="recipe-unit">Unidad</label>
                    <input id="recipe-unit" type="text" value={ingredientUnit} readOnly />
                  </div>
                  <button
                    type="button"
                    onClick={addIngredient}
                    className="btn btn-sm btn-secondary"
                    disabled={!selectedIngredientId || !ingredientQty}
                    aria-label={t('menu.addIngredient')}
                  >
                    +
                  </button>
                </div>

                {formData.recipe && formData.recipe.length > 0 ? (
                  <ul className="list-group">
                    {formData.recipe.map((ing, idx) => (
                      <li key={idx} className="list-group-item">
                        <span>
                          {ing.name} — {ing.quantity} {ing.unit}
                          {ing.cost > 0 && (
                            <span className="text-muted ml-2">(${ing.cost.toFixed(2)})</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeIngredient(idx)}
                          className="btn btn-sm btn-danger"
                        >
                          x
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted small">No hay ingredientes definidos.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Configuración de Combo (solo combo) ── */}
        {formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.combo ? 'collapsed' : ''}`}
              onClick={() => toggleSection('combo')}
              aria-expanded={!collapsedSections.combo}
            >
              <h2>Configuración de Combo</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.combo ? 'section-body--collapsed' : ''}`}
            >
              <div className="combo-builder">
                {/* Fixed Items */}
                <div className="combo-fixed-items">
                  <h5>Items Fijos (Incluidos)</h5>
                  <div className="recipe-form-row">
                    <select
                      value={selectedFixedCategory}
                      onChange={(e) => setSelectedFixedCategory(e.target.value)}
                      className="flex-1"
                      aria-label={t('menu.comboFixedCategory')}
                    >
                      <option value="">{t('menu.selectCategory')}</option>
                      {categories
                        .filter((c) => c.items && c.items.length > 0)
                        .map((c) => (
                          <option key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <select
                      value={selectedFixedItem}
                      onChange={(e) => setSelectedFixedItem(e.target.value)}
                      disabled={!selectedFixedCategory}
                      className="flex-1"
                      aria-label={t('menu.comboFixedItem')}
                    >
                      <option value="">{t('menu.selectItem')}</option>
                      {selectedFixedCategory &&
                        categories
                          .find((c) => c.categoryId === selectedFixedCategory)
                          ?.items.map((i) => (
                            <option key={i.itemId} value={i.itemId}>
                              {i.name}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      onClick={addFixedItem}
                      className="btn btn-sm btn-primary"
                      disabled={!selectedFixedItem}
                    >
                      Agregar
                    </button>
                  </div>

                  {formData.comboItems?.fixed?.length > 0 ? (
                    <ul className="list-group">
                      {formData.comboItems.fixed.map((item, idx) => (
                        <li key={idx} className="list-group-item">
                          {item.name} (x{item.quantity})
                          <button
                            type="button"
                            onClick={() => removeFixedItem(idx)}
                            className="btn btn-sm btn-danger"
                          >
                            X
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted small">No hay items fijos.</p>
                  )}
                </div>

                {/* Choice Groups */}
                <h5>Grupos de Opciones</h5>
                <div className="combo-new-group">
                  <h6>Nuevo Grupo</h6>
                  <label htmlFor="combo-group-name" className="sr-only">
                    {t('menu.comboGroupName')}
                  </label>
                  <input
                    id="combo-group-name"
                    type="text"
                    placeholder="Nombre del Grupo (ej. Bebidas)"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  />
                  <div className="combo-min-max-row">
                    <label>
                      Min:{' '}
                      <input
                        type="number"
                        min="0"
                        value={newGroup.min}
                        onChange={(e) =>
                          setNewGroup({ ...newGroup, min: parseInt(e.target.value) })
                        }
                      />
                    </label>
                    <label>
                      Max:{' '}
                      <input
                        type="number"
                        min="1"
                        value={newGroup.max}
                        onChange={(e) =>
                          setNewGroup({ ...newGroup, max: parseInt(e.target.value) })
                        }
                      />
                    </label>
                  </div>
                  <div className="combo-item-select-row">
                    <select
                      value={selectedGroupCategory}
                      onChange={(e) => setSelectedGroupCategory(e.target.value)}
                      aria-label={t('menu.comboGroupCategory')}
                    >
                      <option value="">{t('menu.selectCategory')}</option>
                      {categories
                        .filter((c) => c.items && c.items.length > 0)
                        .map((c) => (
                          <option key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <select
                      value={selectedGroupItem}
                      onChange={(e) => setSelectedGroupItem(e.target.value)}
                      disabled={!selectedGroupCategory}
                      aria-label={t('menu.comboGroupItem')}
                    >
                      <option value="">{t('menu.selectItem')}</option>
                      {selectedGroupCategory &&
                        categories
                          .find((c) => c.categoryId === selectedGroupCategory)
                          ?.items.map((i) => (
                            <option key={i.itemId} value={i.itemId}>
                              {i.name}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      onClick={addGroupItem}
                      className="btn btn-sm btn-secondary"
                      disabled={!selectedGroupItem}
                      aria-label={t('menu.addGroupItem')}
                    >
                      +
                    </button>
                  </div>
                  {newGroup.items.length > 0 && (
                    <ul className="combo-group-items-preview">
                      {newGroup.items.map((i, idx) => (
                        <li key={idx}>{i.name}</li>
                      ))}
                    </ul>
                  )}
                  <button type="button" onClick={addGroup} className="btn btn-sm btn-success">
                    Agregar Grupo al Combo
                  </button>
                </div>

                {formData.comboItems?.groups?.length > 0 &&
                  formData.comboItems.groups.map((g, idx) => (
                    <div key={idx} className="combo-group-card">
                      <div>
                        <strong>{g.name}</strong> (Min: {g.min}, Max: {g.max})
                        <ul>
                          {g.items.map((i, iIdx) => (
                            <li key={iIdx}>{i.name}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeGroup(idx)}
                        className="btn btn-sm btn-danger"
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tags y Alérgenos ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.tagsAllergens ? 'collapsed' : ''}`}
            onClick={() => toggleSection('tagsAllergens')}
            aria-expanded={!collapsedSections.tagsAllergens}
          >
            <h2>Tags y Alérgenos</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.tagsAllergens ? 'section-body--collapsed' : ''}`}
          >
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="item-tags">
                  Tags <span className="optional-hint">({t('common.optional')})</span>
                </label>
                <input
                  id="item-tags"
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="vegano, sin gluten, picante"
                />
                <span className="help-text">Separados por coma</span>
              </div>
              <div className="form-group">
                <label htmlFor="item-allergens">
                  Alérgenos <span className="optional-hint">({t('common.optional')})</span>
                </label>
                <input
                  id="item-allergens"
                  type="text"
                  value={formData.allergens}
                  onChange={(e) => setFormData({ ...formData, allergens: e.target.value })}
                  placeholder="gluten, lácteos, frutos secos"
                />
                <span className="help-text">Separados por coma</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Disponibilidad ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.availability ? 'collapsed' : ''}`}
            onClick={() => toggleSection('availability')}
            aria-expanded={!collapsedSections.availability}
          >
            <h2>Disponibilidad</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.availability ? 'section-body--collapsed' : ''}`}
          >
            <AvailabilitySchedule
              value={formData.availability}
              onChange={(availability) => setFormData({ ...formData, availability })}
              label=""
            />
          </div>
        </div>

        {/* ── Modificadores (solo estándar) ── */}
        {!formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.modifiers ? 'collapsed' : ''}`}
              onClick={() => toggleSection('modifiers')}
              aria-expanded={!collapsedSections.modifiers}
            >
              <h2>{t('menu.modifierGroups', 'Grupos de Modificadores')}</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.modifiers ? 'section-body--collapsed' : ''}`}
            >
              <div className="modifier-groups-list">
                {modifierGroups.slice(0, visibleModifierCount).map((group) => (
                  <div key={group.groupId} className="form-check">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        checked={(formData.modifierGroups || []).includes(group.groupId)}
                        onChange={() => toggleModifierGroup(group.groupId)}
                      />
                      <span className="modifier-group-info">
                        <span className="modifier-group-name">{group.name}</span>
                        <span className="modifier-group-hint">
                          {group.required
                            ? t('common.required', 'Obligatorio')
                            : t('common.optional', 'Opcional')}
                          {' · '}
                          {t('modifiers.minSelect', 'Mín')}: {group.minSelect} /{' '}
                          {t('modifiers.maxSelect', 'Máx')}: {group.maxSelect}
                          {group.options?.length > 0 &&
                            ` · ${group.options.map((o) => o.name).join(', ')}`}
                        </span>
                      </span>
                    </label>
                  </div>
                ))}
                {visibleModifierCount < modifierGroups.length && (
                  <div ref={modifierSentinelRef} className="modifier-sentinel" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Promociones y Destacados ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.promotions ? 'collapsed' : ''}`}
            onClick={() => toggleSection('promotions')}
            aria-expanded={!collapsedSections.promotions}
          >
            <h2>Promociones y Destacados</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.promotions ? 'section-body--collapsed' : ''}`}
          >
            <div className="promo-builder">
              <div className="promo-checkboxes-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                  />
                  <span>Destacado (Stories)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isDailySpecial}
                    onChange={(e) => setFormData({ ...formData, isDailySpecial: e.target.checked })}
                  />
                  <span>Del Día</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPromotion}
                    onChange={(e) => setFormData({ ...formData, isPromotion: e.target.checked })}
                  />
                  <span>Es Promoción</span>
                </label>
              </div>

              {formData.isFeatured && (
                <div className="featured-order">
                  <div className="form-group">
                    <label htmlFor="item-story-order">Orden en Stories</label>
                    <input
                      id="item-story-order"
                      type="number"
                      min="0"
                      value={formData.storyOrder}
                      onChange={(e) =>
                        setFormData({ ...formData, storyOrder: parseInt(e.target.value) || 0 })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              {formData.isPromotion && (
                <div className="promo-details">
                  <div className="form-group">
                    <label htmlFor="item-original-price">Precio Original</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        id="item-original-price"
                        type="number"
                        step="1"
                        value={formData.originalPrice}
                        onChange={(e) =>
                          setFormData({ ...formData, originalPrice: e.target.value })
                        }
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="item-promo-type">Tipo de Promoción</label>
                    <select
                      id="item-promo-type"
                      value={formData.promotionType}
                      onChange={(e) => setFormData({ ...formData, promotionType: e.target.value })}
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="descuento">Descuento</option>
                      <option value="happy_hour">Happy Hour</option>
                      <option value="2x1">2x1</option>
                      <option value="precio_especial">Precio Especial</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="item-promo-badge">Badge (texto del badge)</label>
                    <input
                      id="item-promo-badge"
                      type="text"
                      value={formData.promotionBadge}
                      onChange={(e) => setFormData({ ...formData, promotionBadge: e.target.value })}
                      placeholder="Ej: -20%, 2x1, Happy Hour"
                    />
                  </div>

                  <div className="form-group">
                    <label id="item-highlight-color-label">Color de Destacado</label>
                    <div className="promo-color-picker">
                      <input
                        type="color"
                        value={formData.highlightColor || '#E91E63'}
                        onChange={(e) =>
                          setFormData({ ...formData, highlightColor: e.target.value })
                        }
                        aria-labelledby="item-highlight-color-label"
                      />
                      <input
                        type="text"
                        value={formData.highlightColor}
                        onChange={(e) =>
                          setFormData({ ...formData, highlightColor: e.target.value })
                        }
                        placeholder="#E91E63"
                        aria-labelledby="item-highlight-color-label"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="item-promo-from">Válida desde</label>
                      <input
                        id="item-promo-from"
                        type="date"
                        value={formData.promotionValidFrom}
                        onChange={(e) =>
                          setFormData({ ...formData, promotionValidFrom: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="item-promo-to">Válida hasta</label>
                      <input
                        id="item-promo-to"
                        type="date"
                        value={formData.promotionValidTo}
                        onChange={(e) =>
                          setFormData({ ...formData, promotionValidTo: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Botones ── */}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary">
            {submitLabel || t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItemForm;
